<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useShiftStore } from '@/stores/shift';
import { useOfflineQueue } from '@/composables/useOfflineQueue';
import { useGpsTracking } from '@/composables/useGpsTracking';
import FeedbackButton from '@/components/FeedbackButton.vue';
import ShiftBar from '@/components/ShiftBar.vue';

const auth = useAuthStore();
const shift = useShiftStore();
const { pending, syncing, flush } = useOfflineQueue();
const { tracking: gpsOn, pointsBuffered } = useGpsTracking();

const online = ref(navigator.onLine);

// El turno ya NO se abre solo: el operario ficha entrada a mano desde la ShiftBar.
// Antes se abría en silencio y sólo para rol 'operario' (Gastón y Daniel son supervisores
// que pickean, así que nunca se les abría y su tiempo muerto no tenía denominador).
// Acá sólo se RESTAURA el turno que ya estuviera abierto, para pintar bien la barra.
onMounted(async () => {
  await auth.restore();
  if (auth.isAuthenticated) await shift.restore();
  window.addEventListener('online', () => {
    online.value = true;
    flush();
  });
  window.addEventListener('offline', () => {
    online.value = false;
  });
});

watch(() => auth.isAuthenticated, async (yes) => {
  if (yes) await shift.restore();
});
</script>

<template>
  <div class="min-h-screen flex flex-col bg-gray-50">
    <!-- Barra de fichaje de turno (visible en todas las pantallas del operario) -->
    <ShiftBar />
    <!-- Banner online/offline / cola pendiente -->
    <div
      v-if="!online || pending > 0"
      class="text-xs text-center py-1.5 px-3"
      :class="!online ? 'bg-yellow-100 text-yellow-900' : 'bg-blue-100 text-blue-900'"
    >
      <template v-if="!online">
        <span>📡 Sin conexión — los escaneos se guardan localmente.</span>
      </template>
      <template v-else>
        <span v-if="syncing">⏫ Sincronizando {{ pending }} evento(s)…</span>
        <span v-else>{{ pending }} evento(s) pendientes de sincronizar.</span>
      </template>
    </div>
    <!-- Indicador de GPS activo (solo si hay shift y consent) -->
    <div
      v-if="gpsOn && pointsBuffered > 0"
      class="text-[10px] text-center py-0.5 bg-gray-100 text-gray-500"
      title="Track de movimiento guardándose"
    >
      📍 {{ pointsBuffered }} pendientes
    </div>

    <main class="flex-1 max-mobile w-full">
      <router-view v-slot="{ Component, route }">
        <transition name="fade" mode="out-in">
          <component :is="Component" :key="route.path" />
        </transition>
      </router-view>
    </main>

    <!-- Botón flotante de feedback — visible en todas las pantallas -->
    <FeedbackButton />
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
